import { apiRequest } from "./apiClient";
import {
  areAndroidHealthFeaturesDisabled,
  isAndroidRestrictedModule,
} from "../utils/platformFeatures";

const assistantCacheKey = (familyId, lang = "tr") =>
  `assistant-summary:${familyId || "none"}:${lang === "en" ? "en" : "tr"}:${areAndroidHealthFeaturesDisabled() ? "android-lite" : "full"}`;
const ASSISTANT_CACHE_TTL_MS = 30000;
const assistantInFlight = new Map();

const stripAndroidRestrictedSummary = (summary) => {
  if (!summary || !areAndroidHealthFeaturesDisabled()) return summary;
  const modules = Object.fromEntries(
    Object.entries(summary.modules || {}).filter(([key]) => !isAndroidRestrictedModule(key))
  );
  const filterItems = (items) =>
    (Array.isArray(items) ? items : []).filter((item) => !isAndroidRestrictedModule(item?.module));

  return {
    ...summary,
    modules,
    priorityItems: filterItems(summary.priorityItems),
    suggestions: filterItems(summary.suggestions),
    crossSuggestions: filterItems(summary.crossSuggestions),
  };
};

export function readCachedAssistantSummary(familyId, lang = "tr") {
  if (!familyId || typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(assistantCacheKey(familyId, lang));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return stripAndroidRestrictedSummary(parsed?.summary || parsed || null);
  } catch (_) {
    return null;
  }
}

function writeCachedAssistantSummary(familyId, lang, summary) {
  if (!familyId || !summary || typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(assistantCacheKey(familyId, lang), JSON.stringify({ savedAt: Date.now(), summary }));
  } catch (_) {
    // Cache best-effort tutulur; uygulama akışını etkilemez.
  }
}

export async function getAssistantSummary(familyId, lang = "tr", { force = false } = {}) {
  if (!familyId) throw new Error("familyId gerekli");
  const key = assistantCacheKey(familyId, lang);

  if (!force && typeof window !== "undefined") {
    try {
      const raw = window.sessionStorage.getItem(key);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.summary && Date.now() - Number(parsed.savedAt || 0) < ASSISTANT_CACHE_TTL_MS) {
          return parsed.summary;
        }
      }
    } catch (_) {}
  }

  if (!force && assistantInFlight.has(key)) return assistantInFlight.get(key);

  const request = (async () => {
    const query = new URLSearchParams();
    if (lang) query.set("lang", lang);
    if (areAndroidHealthFeaturesDisabled()) query.set("scope", "android-lite");
    const q = query.toString() ? `?${query.toString()}` : "";
    const data = await apiRequest(`/assistant/${encodeURIComponent(familyId)}/summary${q}`);
    const summary = stripAndroidRestrictedSummary(data?.summary || null);
    writeCachedAssistantSummary(familyId, lang, summary);
    return summary;
  })();

  assistantInFlight.set(key, request);
  try {
    return await request;
  } finally {
    if (assistantInFlight.get(key) === request) assistantInFlight.delete(key);
  }
}

export function buildLocalAssistantFallback(appData = {}, props = {}) {
  const lang = props.lang === "en" ? "en" : "tr";
  const tasks = appData.gorevler || appData.tasks || [];
  const shopping = appData.alisveris || appData.shopping || [];
  const calendar = appData.yaklasanlar || appData.calendar || [];
  const pendingShopping = shopping.filter((x) => !x.alindi && x.status !== "completed").length;
  const openTasks = tasks.filter((x) => !x.tamamlandi && x.status !== "completed").length;
  const todayEvents = calendar.length;
  const monthEvents = Number(props.toplamEtkinlikBuAy || props.monthEvents || todayEvents || 0);
  const places = Number(props.toplamOrtakYer || 0);
  const geofences = Number(props.toplamSinir || 0);
  const safeZones = Number(props.toplamGuvenliAlan || 0);
  const locationPoints = Number(props.toplamKonumGecmisi || 0);
  const roomCount = Number(props.toplamSohbet || 0);
  const totalNotifications = Number(props.toplamBildirim || props.toplamOkunmamisBildirim || 0);
  const unreadNotifications = Number(props.toplamOkunmamisSistemBildirim || props.toplamOkunmamisBildirim || 0);
  const chatNotifications = Number(props.toplamOkunmamisSohbetBildirim || 0);
  return {
    score: 80,
    headline: lang === "en" ? "Your daily family overview is ready." : "Günlük aile özetiniz hazır.",
    shortText: lang === "en"
      ? `${openTasks} tasks, ${pendingShopping} shopping items and ${todayEvents} events are on the radar.`
      : `${openTasks} görev, ${pendingShopping} alışveriş ürünü ve ${todayEvents} etkinlik takipte.`,
    attentionCount: openTasks + pendingShopping + todayEvents,
    priorityItems: [],
    modules: {
      tasks: { open: openTasks, today: 0, overdue: 0, shortText: lang === "en" ? `${openTasks} active tasks.` : `${openTasks} aktif görev var.` },
      shopping: { pending: pendingShopping, shortText: lang === "en" ? `${pendingShopping} shopping items are waiting.` : `Alışverişte ${pendingShopping} ürün bekliyor.` },
      calendar: { today: todayEvents, week: todayEvents, month: monthEvents, shortText: lang === "en" ? `${todayEvents} upcoming events, ${monthEvents} this month.` : `${todayEvents} yaklaşan etkinlik, bu ay ${monthEvents} etkinlik var.` },
      finance: { net: Number(props.netBakiye || 0), income: 0, expense: 0, shortText: lang === "en" ? "Finance summary is available." : "Finans özeti hazır." },
      sharedExpenses: { monthTotal: Number(props.toplamOrtakHarcama || 0), count: 0, shortText: lang === "en" ? "Shared expenses summary is ready." : "Ortak harcama özeti hazır." },
      ...(!areAndroidHealthFeaturesDisabled() ? {
        health: { total: Number(props.toplamSaglikKaydi || 0), shortText: lang === "en" ? "Health records summary is ready." : "Sağlık kayıtları özeti hazır." },
      } : {}),
      documents: { total: Number(props.toplamBelge || 0), due30: 0, shortText: lang === "en" ? "Documents summary is ready." : "Belge özeti hazır." },
      ...(!areAndroidHealthFeaturesDisabled() ? {
        children: { total: Number(props.cocukKartSayisi || 0), records: Number(props.cocukTakipKaydi || 0), shortText: lang === "en" ? "Child tracking summary is ready." : "Çocuk takibi özeti hazır." },
      } : {}),
      map: { activeShares: Number(props.aktifKonumPaylasan || 0), places, geofences, safeZones, locationPoints, shortText: lang === "en" ? `${Number(props.aktifKonumPaylasan || 0)} sharing, ${places} places, ${geofences} geofences, ${safeZones} safe zones, ${locationPoints} location count.` : `${Number(props.aktifKonumPaylasan || 0)} kişi konum paylaşıyor, ${places} ortak yer, ${geofences} sınır, ${safeZones} güvenli alan, ${locationPoints} konum sayısı.` },
      chat: { unread: Number(props.okunmamisChat || 0), roomCount, shortText: lang === "en" ? `${roomCount} chats, ${Number(props.okunmamisChat || 0)} unread messages.` : `${roomCount} sohbet, ${Number(props.okunmamisChat || 0)} okunmamış mesaj.` },
      notifications: { notificationCount: totalNotifications, unread: unreadNotifications, chatNotifications, shortText: lang === "en" ? `${totalNotifications} notifications, ${unreadNotifications} unread, ${chatNotifications} chat notifications.` : `${totalNotifications} bildirim, ${unreadNotifications} okunmamış, ${chatNotifications} sohbet bildirimi.` }
    },
    suggestions: [{ type: "info", module: "general", text: lang === "en" ? "Add more family records to enrich Assistant insights." : "Asistan özetini zenginleştirmek için yeni kayıtlar ekleyebilirsiniz." }],
    crossSuggestions: [],
    generatedAt: new Date().toISOString(),
    isFallback: true
  };
}
